/**
 * Area denial skill for Conductor enemies — persistent damage zone with tick-based damage.
 * Windup (growing telegraph) → active (damage zone) → recovery → cooldown.
 * Damage ticks on a fixed interval during active phase, not per-frame.
 */
import { PhasedSkillEffect } from '../PhasedSkillEffect';
import type { SkillPhase } from '../PhasedSkillEffect';
import type { EnemySkillEffect, EnemySkillMovement, EnemySkillDamageInfo } from './EnemySkillEffect';

export interface EnemyAreaConfig {
  areaRadius: number;
  areaDamagePerTick: number;
  tickIntervalMs: number;
  windupMs: number;
  activeDurationMs: number;
  recoveryMs: number;
  cooldownMs: number;
  range: number;
  attackType: string;
}

/** Area-of-effect attack that creates a persistent damage zone around the caster. */
export class EnemyAreaSkillEffect extends PhasedSkillEffect implements EnemySkillEffect {
  readonly skillId: string;
  protected readonly phaseSequence = ['windup', 'active', 'recovery', 'cooldown'] as const;
  private readonly config: EnemyAreaConfig;

  /** Zone center — captured at activation. */
  private _centerX: number = 0;
  private _centerY: number = 0;

  /** Tick timer for damage intervals during active phase. */
  private _tickTimer: number = 0;

  /** Whether a damage tick is ready to be consumed this frame. */
  private _damageReady: boolean = false;

  constructor(skillId: string, config: EnemyAreaConfig) {
    super();
    this.skillId = skillId;
    this.config = config;
  }

  protected getPhaseDuration(phase: SkillPhase): number {
    switch (phase) {
      case 'windup': return this.config.windupMs;
      case 'active': return this.config.activeDurationMs;
      case 'recovery': return this.config.recoveryMs;
      case 'cooldown': return this.config.cooldownMs;
      default: return 0;
    }
  }

  protected onPhaseEnter(phase: SkillPhase): void {
    if (phase === 'active') {
      this._tickTimer = 0;
      this._damageReady = true; // First tick fires immediately
    }
  }

  protected onUpdate(delta: number): void {
    // Clear damage-ready flag from previous frame
    this._damageReady = false;

    // Tick damage interval during active phase
    if (this.phase === 'active') {
      this._tickTimer += delta;
      if (this._tickTimer >= this.config.tickIntervalMs) {
        this._tickTimer -= this.config.tickIntervalMs;
        this._damageReady = true;
      }
    }
  }

  get skillRange(): number { return this.config.range; }

  /** Set the zone center before activation. */
  setCenter(x: number, y: number): void {
    this._centerX = x;
    this._centerY = y;
  }

  /** True during active phase when a damage tick is ready. */
  isInDamagePhase(): boolean {
    return this.phase === 'active' && this._damageReady;
  }

  /** Stand still during entire skill sequence. */
  getMovement(): EnemySkillMovement | null {
    if (this.phase === 'windup' || this.phase === 'active' || this.phase === 'recovery') {
      return { vx: 0, vy: 0 };
    }
    return null;
  }

  /** Returns zone-centered damage info when a tick is ready. */
  getDamageInfo(): EnemySkillDamageInfo | null {
    if (!this.isInDamagePhase()) return null;
    return {
      baseDamage: this.config.areaDamagePerTick,
      attackType: this.config.attackType,
      range: this.config.areaRadius,
      centerX: this._centerX,
      centerY: this._centerY,
    };
  }
}
